import {
  TRACKED_FEATURES,
  trackFeatureStepCompleted,
} from '@cartwhl/tracking/featureTracking';
import { ReactNode, RefObject, useRef } from 'react';
import { useSnapshot } from 'valtio';

import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import FaceIcon from '@cartwhl/ui/icons/FaceIcon';
import FileIcon from '@cartwhl/ui/icons/FileIcon';

import {
  GEN_SUBPANEL_KEY,
  UPLOAD_CHARACTER_SUBPANEL_KEY,
  UPLOAD_CHARACTER_TAB,
} from 'components/CharacterPanel/constants';
import { characterPickerStore } from 'stores';
import { uiStore } from 'stores';

import { CHAR_GEN_DESKTOP_MIN_WIDTH } from '../constants';
import GenerateCharacterSubpanel from './GenerateCharacterSubpanel';
import UploadCharacterSubpanel from './UploadNewCharacterSubpanel';

export default function CharacterCreationOptions() {
  const { subPanelKey } = useSnapshot(uiStore.state);
  const isGenerateOpen = subPanelKey === GEN_SUBPANEL_KEY;
  const isUploadOpen = subPanelKey === UPLOAD_CHARACTER_SUBPANEL_KEY;

  const generateBtnRef = useRef<HTMLButtonElement>(null!);
  const uploadBtnRef = useRef<HTMLButtonElement>(null!);

  const isMobile = !useMediaQuery(
    `(min-width: ${CHAR_GEN_DESKTOP_MIN_WIDTH}px)`,
  );

  const { imageGenerationImages, selectedImageIndex } = useSnapshot(
    characterPickerStore.state,
  );

  return (
    <>
      <div
        className={`relative flex gap-2 ${isMobile ? 'flex-row' : 'flex-col'} ${!isMobile ? 'border-gray-30 dark:border-gray-60 rounded-2xl border p-2' : ''} `}
      >
        {/* Upload button */}
        <CharacterCreationButton
          icon={<FileIcon />}
          isOpen={isUploadOpen}
          onClick={() => {
            const opening = subPanelKey !== UPLOAD_CHARACTER_SUBPANEL_KEY;
            if (opening) {
              trackFeatureStepCompleted(
                TRACKED_FEATURES.CHARACTER_UPLOAD,
                'upload_panel_opened',
                { metadata: { entry_point: 'character_panel' } },
              );
            }
            uiStore.setSubPanelKey(
              opening ? UPLOAD_CHARACTER_SUBPANEL_KEY : '',
            );
            uiStore.setSubPanelSectionKey(opening ? UPLOAD_CHARACTER_TAB : '');
          }}
          ref={uploadBtnRef}
        >
          {isMobile ? 'Upload' : 'Upload your character'}
        </CharacterCreationButton>

        {/* Generate button */}
        <CharacterCreationButton
          icon={
            imageGenerationImages.length > 0 && selectedImageIndex != null ? (
              <img
                src={imageGenerationImages[selectedImageIndex]}
                crossOrigin="use-credentials"
                className="border-gray-30 dark:border-gray-60 h-7 w-7 rounded-md border object-cover"
              />
            ) : (
              <FaceIcon className="-ml-px h-6 w-6" />
            )
          }
          isOpen={isGenerateOpen}
          onClick={() =>
            uiStore.setSubPanelKey(isGenerateOpen ? '' : GEN_SUBPANEL_KEY)
          }
          ref={generateBtnRef}
        >
          {isMobile ? 'Generate' : 'Generate new character'}
        </CharacterCreationButton>
      </div>

      {isUploadOpen && (
        <UploadCharacterSubpanel
          id={UPLOAD_CHARACTER_SUBPANEL_KEY}
          elementToTrack={uploadBtnRef.current}
        />
      )}
      {isGenerateOpen && (
        <GenerateCharacterSubpanel
          id={GEN_SUBPANEL_KEY}
          elementToTrack={generateBtnRef.current}
        />
      )}
    </>
  );
}

function CharacterCreationButton({
  children,
  icon,
  isOpen,
  onClick,
  ref,
}: {
  children: ReactNode;
  icon: ReactNode;
  isOpen: boolean;
  onClick: () => void;
  ref: RefObject<HTMLButtonElement>;
}) {
  return (
    <button
      className={`relative inline-flex h-10 items-center justify-between gap-2 rounded-lg px-2 text-center transition-all duration-200 ease-in-out hover:no-underline active:scale-[0.98] ${
        isOpen
          ? 'bg-blue text-white dark:text-white'
          : 'bg-gray-15 text-gray-90 dark:bg-gray-75 dark:text-gray-15 hover:bg-gray-30 dark:hover:bg-gray-60'
      } `}
      onClick={onClick}
      ref={ref}
    >
      <span className="flex-none">{icon}</span>

      <span className={`truncate pt-[0.1em] text-[14px]/[14px] font-medium`}>
        {children}
      </span>

      <span
        className={`flex-none transition-colors ${isOpen ? 'text-white' : 'text-gray-60'}`}
      >
        <ChevronRightIcon />
      </span>
    </button>
  );
}
